// Location Service for GPS and real geocoding

export class LocationService {

  // Get user's current GPS location
  static getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('GPS not supported by this browser. Please enter pincode manually.'));
        return;
      }

      const options = {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 300000 // 5 minutes cache
      };

      if (document.visibilityState === 'hidden') {
        reject(new Error('Please keep the page active while getting location'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          resolve({
            lat: latitude,
            lng: longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permission in your browser settings or enter pincode manually.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable. Please check your GPS settings or use pincode instead.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again or enter pincode manually.';
              break;
            default:
              errorMessage = 'GPS error occurred. Please enter your pincode manually.';
          }
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  // Reverse geocode coordinates using OpenStreetMap Nominatim (free, no API key)
  // This is display-only — GPS doctor fetch is not dependent on this succeeding.
  static async reverseGeocode(lat, lng) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' }, signal: controller.signal }
      );
      clearTimeout(timeout);
      if (!response.ok) throw new Error('Geocoding request failed');
      const data = await response.json();
      const addr = data.address || {};
      const city = addr.city || addr.town || addr.village || addr.county || '';
      const state = addr.state || '';
      const pincode = addr.postcode || '';
      const locality = addr.suburb || addr.neighbourhood || addr.road || '';
      return {
        locality,
        city,
        state,
        country: addr.country || 'India',
        pincode,
        formattedAddress: data.display_name || `${city}, ${state} ${pincode}`
      };
    } catch (error) {
      clearTimeout(timeout);
      // Fallback — does not block GPS doctor fetch which already fired
      return {
        locality: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        formattedAddress: `Lat ${lat.toFixed(4)}, Lng ${lng.toFixed(4)}`
      };
    }
  }

  // Look up pincode info using India Post API (free, no API key, covers all Indian pincodes)
  static async getLocationFromPincode(pincode) {
    if (!/^\d{6}$/.test(pincode)) {
      throw new Error('Invalid pincode format. Please enter 6 digits.');
    }
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      if (!response.ok) throw new Error('Pincode lookup failed');
      const data = await response.json();
      if (data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
        const po = data[0].PostOffice[0];
        return {
          city: po.District,
          state: po.State,
          locality: po.Name,
          area: po.Block || po.Division || '',
          country: 'India',
          pincode,
          formattedAddress: `${po.Name}, ${po.District}, ${po.State} ${pincode}`
        };
      }
      throw new Error('Pincode not found. Please verify and try again.');
    } catch (error) {
      throw error;
    }
  }

  // Validate Indian pincode format
  static isValidPincode(pincode) {
    return /^\d{6}$/.test(pincode);
  }

  // Calculate distance between two coordinates (for doctor proximity)
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  }

  static toRad(degrees) {
    return degrees * (Math.PI / 180);
  }
}

export default LocationService;